import { Battery, BatteryCharging, BatteryPlus, Clock, Fuel, Gauge, LifeBuoy, LockKeyhole, MapPin, Phone } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Reveal } from "./Reveal";
import { SERVICES, PHONE_DISPLAY, PHONE_HREF } from "../lib/site";

const ICONS = { Battery, BatteryCharging, BatteryPlus, Fuel, Gauge, LifeBuoy, LockKeyhole };

const InfoTip = ({ text, id }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span role="button" tabIndex={0} data-testid={`info-${id}`} className="info-dot ml-2 align-middle" aria-label="More info">
          !
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-[#1A1A1A] text-neutral-200 border border-white/15 font-sans text-sm p-3 rounded-none">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ServiceCard = ({ service }) => {
  const Icon = ICONS[service.icon];
  return (
    <div
      data-testid={`service-row-${service.id}`}
      className="border border-white/10 bg-[#0D0D0D] p-6 md:p-7 flex items-center gap-5 transition-colors duration-300 hover:border-primary/40"
    >
      <span data-testid={`service-icon-${service.id}`} className="shrink-0 text-primary">
        {Icon && <Icon size={40} strokeWidth={1.6} />}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-bold uppercase tracking-normal text-lg md:text-xl leading-tight">
          {service.label}
          {service.tooltip && <InfoTip text={service.tooltip} id={service.id} />}
        </h3>
        <p className="mt-1.5 text-sm text-neutral-400 leading-snug">{service.blurb}</p>
      </div>
      <div className="shrink-0 text-right">
        <span className="font-display font-bold text-3xl md:text-4xl text-primary leading-none">${service.price}</span>
        {service.suffix && <span className="block font-mono text-[11px] uppercase tracking-wider text-primary/80 mt-1">{service.suffix}</span>}
      </div>
    </div>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <Reveal>
        <p className="eyebrow mb-4 text-center">01 — Upfront Pricing</p>
        <h2 className="font-display font-bold uppercase tracking-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-center">
          <span className="block">The price you see is</span>
          <span className="block">the price you pay.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-14 grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <div className="border border-white/10 overflow-hidden">
            <img src="/images/van.png" alt="Tru North roadside assistance service van at night" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="border border-white/10 overflow-hidden">
            <img src="/images/flatbed.png" alt="Flatbed tow truck detail with amber beacon glow" className="w-full aspect-[4/3] object-cover" />
          </div>
        </div>
        <p className="mt-5 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          One van. One technician. Fully stocked.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-16 flex items-center gap-6" data-testid="services-pricing-header">
          <span className="flex-1 border-t-2 border-primary/70" />
          <h3 className="font-display font-bold uppercase tracking-normal text-2xl md:text-4xl whitespace-nowrap">Our Services &amp; Pricing</h3>
          <span className="flex-1 border-t-2 border-primary/70" />
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="services-grid">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
          <a
            href={PHONE_HREF}
            data-testid="services-call-card"
            className="border border-primary bg-primary p-6 md:p-7 flex items-center gap-5 transition-transform duration-300 hover:-translate-y-1"
          >
            <span className="shrink-0 text-black">
              <Phone size={40} strokeWidth={1.6} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold uppercase tracking-normal text-lg md:text-xl text-black leading-tight">Get Help Now</p>
              <p className="mt-1.5 text-sm text-black/70 leading-snug">24/7 — we answer, we quote, we roll.</p>
            </div>
            <span className="shrink-0 text-right font-display font-bold text-xl md:text-2xl text-black leading-none">{PHONE_DISPLAY}</span>
          </a>
        </div>

        <div className="mt-10 border border-white/10 bg-[#0D0D0D] grid md:grid-cols-2" data-testid="fee-panel">
          <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10" data-testid="mileage-card">
            <p className="flex items-center gap-3 font-display font-bold uppercase tracking-normal text-lg md:text-xl">
              <MapPin size={26} className="text-primary" /> Mileage
            </p>
            <div className="mt-5 space-y-3 text-sm">
              <p className="flex items-baseline justify-between gap-4">
                <span className="text-neutral-300">Zones 1 – 3</span>
                <span className="font-mono uppercase tracking-wider text-primary font-semibold">Included</span>
              </p>
              <p className="flex items-baseline justify-between gap-4">
                <span className="text-neutral-300">Zones 4 – 6</span>
                <span className="text-right"><span className="font-mono uppercase tracking-wider text-primary font-semibold">$3/mi</span> <span className="text-neutral-500 text-xs">after first 15 miles</span></span>
              </p>
              <p className="flex items-baseline justify-between gap-4">
                <span className="text-neutral-300">Zone 7</span>
                <span className="font-mono uppercase tracking-wider text-primary font-semibold">Custom quote</span>
              </p>
            </div>
          </div>
          <div className="p-6 md:p-8" data-testid="after-hours-card">
            <p className="flex items-center gap-3 font-display font-bold uppercase tracking-normal text-lg md:text-xl">
              <Clock size={26} className="text-primary" /> After-Hours
            </p>
            <div className="mt-5 flex items-center gap-6">
              <p className="shrink-0">
                <span className="font-display font-bold text-5xl text-primary leading-none">$0</span>
                <span className="block font-mono text-[11px] uppercase tracking-wider text-primary/80 mt-1">surcharge</span>
              </p>
              <p className="text-sm text-neutral-400 leading-snug">Same price nights, weekends, and holidays.</p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
